import { AbandonCartNudgeTrayModelState } from './abandon-cart-nudge-tray.model'

interface AbandonCartNudgeTrayViewProps {
  win: Window
  onCta: () => void
  onClose: () => void
  isOpen: boolean
}

export default class AbandonCartNudgeTrayView {
  private props: AbandonCartNudgeTrayViewProps
  private el?: HTMLElement
  private pendingState?: AbandonCartNudgeTrayModelState
  private destroyed = false

  constructor(props: AbandonCartNudgeTrayViewProps) {
    this.props = props
    this.init()
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    if (this.el) {
      this.el.removeEventListener('cl-nudge-tray--cta', this.props.onCta)
      this.el.removeEventListener('cl-nudge-tray--close', this.props.onClose)
      this.el.remove()
      this.el = undefined
    }
  }

  public open = (state: AbandonCartNudgeTrayModelState): void => {
    this.pendingState = state

    if (!state.config.enabled) {
      return
    }

    if (!this.el) {
      return
    }

    this.setCtaBg(state)
    this.setContent(state)
    this.setAriaLabel(state)
    this.setOpen(true)
  }

  public close = (): void => {
    this.setOpen(false)
  }

  private init = async () => {
    try {
      await import('@classy/embedded-components/dist/ClassyNudgeTray.js')

      this.el = this.props.win.document.createElement('cl-nudge-tray')
      this.el.setAttribute('role', 'complementary')

      this.props.win.document.body.appendChild(this.el)

      this.el.addEventListener('cl-nudge-tray--cta', this.props.onCta)
      this.el.addEventListener('cl-nudge-tray--close', this.props.onClose)

      // To be able to see the animation
      setTimeout(() => {
        if (this.pendingState && this.pendingState.config.enabled) {
          this.setCtaBg(this.pendingState)
          this.setContent(this.pendingState)
          this.setAriaLabel(this.pendingState)
        }
        this.setOpen(this.props.isOpen)
      }, 0)
    } catch (_) {
      console.log(_)
      console.error('unable to init the abandon cart nudge tray view')
    }
  }

  private setContent = (state: AbandonCartNudgeTrayModelState) => {
    if (!this.el) {
      return
    }

    const amount = Number(state.nav.params.amount)

    if (isNaN(amount) || !isFinite(amount) || amount <= 0) {
      return
    }

    this.el.setAttribute('content', `Finish your $${amount} gift to create an impact today.`)
  }

  private setCtaBg = (state: AbandonCartNudgeTrayModelState) => {
    if (!this.el) {
      return
    }

    this.el.setAttribute('brand-color', state.config.theme['--theme-color__brand-primary'])
  }

  private setAriaLabel = (state: AbandonCartNudgeTrayModelState) => {
    if (!this.el) {
      return
    }

    const ariaLabel = state.config.organizationName
      ? `Donate to ${state.config.organizationName}`
      : 'Donate'
    this.el.setAttribute('aria-label', ariaLabel)
  }

  private setOpen = (open: boolean) => {
    if (!this.el) {
      return
    }

    this.el.setAttribute('open', open ? 'true' : 'false')
  }
}
